import { Typography } from '@/ui/atoms/typography/Typography'
import { Badge, GradientBadge } from '@/ui/atoms/badge/Badge'
import { useAccount } from '@/domain/hooks/useAccount'
import { cn } from '@/ui/utils/style'
import { useMemo, useState } from 'react'
import { Button } from '@/ui/atoms/button/Button'
import { TableCell, TableRow } from '@/ui/atoms/table/Table'
import { DataTable } from '@/ui/molecules/data-table/DataTable'
import { Panel } from '@/ui/atoms/panel/Panel'
import { usePointsLeaderboard } from '@/features/points/logic/seasonMew/usePointsLeaderboard'
import { useUserPoints } from '@/features/points/logic/seasonMew/useUserPoints'
import { KNOWN_PROTOCOLS } from '@/config/consts'

const ITEMS_PER_PAGE = 10

export function Leaderboard() {
  const { pointsLeaderboard } = usePointsLeaderboard()
  const { data: userPoints } = useUserPoints()
  const leaderboard = pointsLeaderboard.results
  const account = useAccount()
  const [page, setPage] = useState(0)

  const paginatedTableData = useMemo(() => {
    const start = page * ITEMS_PER_PAGE
    const end = start + ITEMS_PER_PAGE
    return leaderboard.slice(start, end)
  }, [page, leaderboard])

  const totalPages = Math.max(1, Math.ceil(leaderboard.length / ITEMS_PER_PAGE))

  return (
    <Panel.Wrapper className="w-full px-3 py-4">
      <div className="mb-6 pl-3">
        <Typography variant="h2" className="text-xl" gradient>
          Leaderboard
        </Typography>
      </div>

      {leaderboard.length ? (
        <div className="w-full">
          <DataTable
            gridTemplateColumnsClassName="grid-cols-[54px_repeat(2,_1fr)] sm:grid-cols-[84px_repeat(2,_1fr)]"
            columnDef={{
              ranking: {
                header: 'Rank',
                headerAlign: 'left',
                showOnMobile: true,
                sortable: true,
                sortingFn: (a, b) => (a.original.rank ?? 0) - (b.original.rank ?? 0),
                renderCell: ({ rank }) => (
                  <div>
                    <div className="flex w-full flex-row justify-start pl-3 text-sm">{rank ? rank : '😻'}</div>
                  </div>
                ),
              },
              recipient: {
                header: 'User',
                headerAlign: 'left',
                showOnMobile: true,
                renderCell: ({ address }) => (
                  <div>
                    <div className="flex w-full flex-col items-start gap-1 text-sm sm:flex-row sm:items-center sm:gap-4">
                      {address.toLowerCase() === account?.toLowerCase() ? (
                        <GradientBadge>
                          <Typography variant="prompt" gradient>
                            You
                          </Typography>
                        </GradientBadge>
                      ) : (
                        <p>
                          {address.slice(0, 5)}...{address.slice(-3)}
                        </p>
                      )}
                      {KNOWN_PROTOCOLS.includes(address.toLowerCase()) && (
                        <Badge>
                          <Typography variant="prompt" className="text-xs">
                            Protocol
                          </Typography>
                        </Badge>
                      )}
                    </div>
                  </div>
                ),
              },
              // points: {
              //   header: 'Points',
              //   headerAlign: 'right',
              //   showOnMobile: true,
              //   sortable: true,
              //   sortingFn: (a, b) => Number(a.original.total_amount) - Number(b.original.total_amount),
              //   renderCell: ({ total_amount }, mobileViewOptions) => {
              //     return (
              //       <div
              //         className={cn(
              //           'flex items-center gap-2',
              //           mobileViewOptions?.isMobileView ? 'justify-center' : 'justify-end',
              //         )}
              //       >
              //         <Typography variant="p" gradient className="font-bold text-sm">
              //           {Number(total_amount).toLocaleString(undefined, {
              //             minimumFractionDigits: 2,
              //             maximumFractionDigits: 2,
              //           })}
              //         </Typography>
              //       </div>
              //     )
              //   },
              // },
              // referralPoints: {
              //   header: 'Referral points',
              //   headerAlign: 'right',
              //   showOnMobile: true,
              //   sortable: true,
              //   sortingFn: (a, b) => a.original.referralPoints - b.original.referralPoints,
              //   renderCell: ({ referralPoints }, mobileViewOptions) => {
              //     return (
              //       <div
              //         className={cn(
              //           'flex items-center gap-2',
              //           mobileViewOptions?.isMobileView ? 'justify-center' : 'justify-end',
              //         )}
              //       >
              //         <Typography variant="p" gradient className="font-bold text-sm">
              //           {Number(referralPoints).toLocaleString(undefined, {
              //             minimumFractionDigits: 2,
              //             maximumFractionDigits: 2,
              //           })}
              //         </Typography>
              //       </div>
              //     )
              //   },
              // },
              totalPoints: {
                header: 'Total points',
                headerAlign: 'right',
                showOnMobile: true,
                sortable: true,
                sortingFn: (a, b) => {
                  return Number(a.original.total_amount) - Number(b.original.total_amount)
                },
                renderCell: ({ total_amount }, mobileViewOptions) => {
                  return (
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        mobileViewOptions?.isMobileView ? 'justify-center' : 'justify-end',
                      )}
                    >
                      <Typography variant="p" gradient className="font-bold text-sm">
                        {Number(total_amount).toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                      </Typography>
                    </div>
                  )
                },
              },
            }}
            data={account && userPoints ? [userPoints, ...paginatedTableData] : paginatedTableData}
            footer={
              totalPages > 1 && (
                <TableRow>
                  <TableCell className="text-center">
                    <div className="flex w-full flex-row items-center justify-center gap-4 pt-4">
                      <Button variant="outline" size="sm" onClick={() => setPage(0)} disabled={page === 0}>
                        First
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                        Previous
                      </Button>
                      <Typography variant="p" className="hidden text-sm sm:block">
                        {page + 1} of {totalPages}
                      </Typography>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages - 1}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page === totalPages - 1}
                      >
                        Last
                      </Button>
                    </div>
                    <Typography variant="p" className="mt-4 text-sm sm:hidden">
                      {page + 1} of {totalPages}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            }
          />
        </div>
      ) : (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-10 text-center">
          <Typography variant="prompt" className="text-sm">
            Leaderboard is currently empty. Check back soon!
          </Typography>
        </div>
      )}
    </Panel.Wrapper>
  )
}
